import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { IoNotificationsOutline } from 'react-icons/io5';
import LanguageToggle from './components/LanguageToggle';
import ThemeToggle from './components/ThemeToggle';
import { useLanguage } from './i18n/LanguageContext';
import { useNotifications } from './NotificationContext';
import { firstDisplayNameFromUser } from './utils/userLocale';
import './TopBar.css';

function formatLoginSession(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 24);
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return String(iso).slice(0, 24);
  }
}

function formatNotifTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return '';
  }
}

const TopBar = ({
  user,
  onProfileUpload,
  onMenuToggle,
  realTimeInfo,
  title,
  sessionStartedAt,
  onLogout,
  onNavigate,
}) => {
  const fileInput = useRef();
  const bellWrapRef = useRef(null);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const { t } = useLanguage();
  const { items, unreadCount, markRead, markAllRead, refreshNotifications } = useNotifications();

  const displayName = useMemo(() => firstDisplayNameFromUser(user), [user]);
  const initials = useMemo(() => {
    const s = (displayName || 'U').replace(/[^a-zA-Z]/g, '').slice(0, 2);
    return (s || 'U').toUpperCase();
  }, [displayName]);

  const sessionIso = sessionStartedAt ?? user?.sessionStartedAt;
  const sessionLabel = useMemo(() => formatLoginSession(sessionIso), [sessionIso]);

  useEffect(() => {
    setAvatarBroken(false);
  }, [user?.profileUrl]);

  useEffect(() => {
    if (!bellOpen) return undefined;
    const onDoc = (e) => {
      if (bellWrapRef.current && !bellWrapRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setBellOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [bellOpen]);

  useEffect(() => {
    if (!user) return undefined;
    const id = window.setInterval(() => {
      refreshNotifications();
    }, 90000);
    return () => window.clearInterval(id);
  }, [user, refreshNotifications]);

  const badgeText = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : '';

  const handleNotifClick = useCallback(
    async (n) => {
      if (!n.read && n.id) {
        try {
          await markRead(n.id);
        } catch {
          /* ignore */
        }
      }
      setBellOpen(false);
      if (n.navigateTo && typeof onNavigate === 'function') {
        onNavigate(n.navigateTo);
      }
    },
    [markRead, onNavigate]
  );

  const handleProfileClick = () => {
    fileInput.current?.click();
  };

  const handleFileChange = (e) => {
    const input = e.target;
    const f = input.files?.[0];
    if (f && typeof onProfileUpload === 'function') {
      onProfileUpload(f);
    }
    input.value = '';
  };

  return (
    <header
      className="topbar"
      title={sessionLabel ? `Logged in: ${sessionLabel}` : undefined}
    >
      <div className="topbar-left">
        {typeof onMenuToggle === 'function' && (
          <button type="button" className="topbar-menu-btn" onClick={onMenuToggle} aria-label={t('topbar.openMenu')}>
            <span aria-hidden>≡</span>
          </button>
        )}
        <span className="topbar-title">{title || t('topbar.dashboard')}</span>
        {realTimeInfo && <span className="topbar-realtime">{realTimeInfo}</span>}
      </div>
      <div className="topbar-center">
        {sessionLabel ? (
          <span className="topbar-session" title="Time of this sign-in">
            {t('topbar.loggedIn')} <time dateTime={sessionIso}>{sessionLabel}</time>
          </span>
        ) : (
          <span className="topbar-session topbar-session--muted">{t('topbar.session')}</span>
        )}
      </div>
      <div className="topbar-right">
        <LanguageToggle variant="topbar" />
        <ThemeToggle variant="topbar" />
        <div className="topbar-bell-wrap" ref={bellWrapRef}>
          <button
            type="button"
            className="topbar-bell-btn"
            aria-label={
              unreadCount ? t('topbar.unreadNotifications', { count: unreadCount }) : t('topbar.notifications')
            }
            aria-expanded={bellOpen}
            aria-haspopup="true"
            onClick={() => {
              setBellOpen((o) => !o);
              if (!bellOpen) refreshNotifications();
            }}
          >
            <IoNotificationsOutline className="topbar-bell-icon" aria-hidden />
            {badgeText ? (
              <span className="topbar-bell-badge" aria-hidden>
                {badgeText}
              </span>
            ) : null}
          </button>
          {bellOpen && (
            <div className="topbar-bell-panel" role="dialog" aria-label={t('topbar.notifications')}>
              <div className="topbar-bell-panel-head">
                <span>{t('topbar.notifications')}</span>
                {unreadCount > 0 ? (
                  <button type="button" className="topbar-bell-markall" onClick={() => markAllRead()}>
                    {t('topbar.markAllRead')}
                  </button>
                ) : null}
              </div>
              <ul className="topbar-bell-list">
                {items.length === 0 ? (
                  <li className="topbar-bell-empty">{t('topbar.noNotifications')}</li>
                ) : (
                  items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        className={`topbar-bell-item${n.read ? '' : ' topbar-bell-item--unread'}`}
                        onClick={() => handleNotifClick(n)}
                      >
                        <span className="topbar-bell-item-title">{n.title || 'cbonline app'}</span>
                        {n.body ? <span className="topbar-bell-item-body">{n.body}</span> : null}
                        <span className="topbar-bell-item-meta">
                          {formatNotifTime(n.createdAt)}
                          {n.navigateTo ? ` · Open: ${n.navigateTo}` : ''}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
        <div
          className="topbar-profile"
          onClick={handleProfileClick}
          title={displayName ? `Profile: ${displayName} — ${t('topbar.changePhoto')}` : t('topbar.changePhoto')}
          role="button"
          tabIndex={0}
        >
          {user?.profileUrl && !avatarBroken ? (
            <img
              src={user.profileUrl}
              alt=""
              className="topbar-profile-img"
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <span className="topbar-profile-initials" aria-hidden>
              {initials}
            </span>
          )}
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
          style={{ display: 'none' }}
          ref={fileInput}
          onChange={handleFileChange}
        />
        <button type="button" className="topbar-logout-btn" onClick={onLogout}>
          {t('topbar.logout')}
        </button>
      </div>
    </header>
  );
};

export default TopBar;
